/**
 * Horsir Office Add-in — taskpane UI.
 * Chat transcript, settings drawer, agent driver (runAgentLoop), history,
 * and apply-actions toolbar. All Office.js host interactions are delegated to
 * src/host/{word,excel,powerpoint}.ts and src/core/clientTools.ts.
 */

import "./taskpane.css";
import { chat, setBackendAvailable } from "../core/llm";
import {
  loadConfig,
  saveConfig,
  isConfigured,
  PRESETS,
  loadSearch,
  saveSearch,
  SearchConfig,
  SearchProvider,
  SEARCH_PRESETS,
  putSearchCredential,
  getSearchCredential,
  loadContextConfig,
  saveContextConfig,
} from "../core/settings";
import { ChatMessage, ContextConfig, HostKind, LLMConfig } from "../core/types";
import * as Word from "../host/word";
import * as Excel from "../host/excel";
import * as Ppt from "../host/powerpoint";
import { renderMarkdown } from "../core/markdown";
import { agentHealth, runAgent, AgentTraceItem } from "../core/agent";
import { cleanForInsertMessages } from "../core/prompts";
import {
  loadNeAuth,
  saveNeAuth,
  clearNeAuth,
  neConfig,
  neAccountLogin,
  neCarsiLogin,
  neFetchModels,
  isQuotaError,
  isNeTokenExpiring,
  tryRefreshNeAuth,
  saveNeCredentials,
  loadNeCredentials,
  clearNeCredentials,
  NeAuth,
  NeUser,
  NeModelInfo,
} from "../core/ne";
import { executeClientTool, TOOL_LABELS } from "../core/clientTools";
import { setLang, getLang, t, tf, L, Lang, applyI18n } from "../core/i18n";
import { BRAND_NAME, BRAND_SLUG, brandDisplay } from "../core/brand";

// ----------------------------------------------------------------------------
// State & tiny DOM helpers
// ----------------------------------------------------------------------------
interface State {
  host: HostKind;
  config: LLMConfig;
  customConfig: LLMConfig;
  ne: NeAuth | null;
  search: SearchConfig;
  contextConfig: ContextConfig;
  lastResult: string;
  busy: boolean;
  chat: ChatMessage[];
  backend: boolean;
  abortController: AbortController | null;
}
const state: State = {
  host: "unknown",
  config: loadConfig(),
  customConfig: loadConfig(),
  ne: loadNeAuth(),
  search: loadSearch(),
  contextConfig: loadContextConfig(),
  lastResult: "",
  busy: false,
  chat: [],
  backend: false,
  abortController: null,
};

/** Cached NE model list (fetched after login / on demand). */
let neModelCache: NeModelInfo[] = [];
/** Tracks whether the last neFetchModels attempt failed, to avoid re-fetch loops. */
let neModelFetchFailed = false;

const $ = <T extends HTMLElement = HTMLElement>(id: string): T => {
  const el = document.getElementById(id);
  if (!el) throw new Error(tf(L.BOOT.missingDom, id));
  return el as T;
};
function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  ...children: (Node | string)[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k === "class") node.className = v;
    else node.setAttribute(k, v);
  }
  for (const c of children) node.append(typeof c === "string" ? document.createTextNode(c) : c);
  return node;
}

function setStatus(msg: string, kind: "" | "error" | "warn" = "") {
  const el = $("statusLine");
  el.textContent = msg;
  el.className = `status-line ${kind}`;
}

function updateCopyBtn() {
  const btn = document.getElementById("btnCopy") as HTMLButtonElement | null;
  if (!btn) return;
  btn.disabled = !state.lastResult.trim();
}
async function copyResult() {
  const text = state.lastResult.trim();
  if (!text) return;
  const ok = await writeClipboard(text);
  flashCopy(ok ? t(L.ACTIONS.copied) : t(L.ACTIONS.copyFailed), !ok);
}
async function writeClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.append(ta);
      ta.select();
      const ok = document.execCommand("copy");
      ta.remove();
      return ok;
    } catch {
      return false;
    }
  }
}
function flashCopy(msg: string, isError = false) {
  const btn = document.getElementById("btnCopy") as HTMLButtonElement | null;
  if (!btn) return;
  const prev = btn.textContent;
  btn.textContent = msg;
  btn.classList.toggle("is-error", isError);
  window.setTimeout(() => {
    btn.textContent = prev;
    btn.classList.remove("is-error");
  }, 1200);
}

// ---- modal confirm (replaces window.confirm) ----
function showConfirm(message: string): Promise<boolean> {
  return new Promise((resolve) => {
    const overlay = $("confirmModal");
    const msg = $("confirmMsg");
    msg.textContent = message;
    overlay.hidden = false;
    let handled = false;
    const done = (ok: boolean) => {
      if (handled) return;
      handled = true;
      overlay.hidden = true;
      resolve(ok);
    };
    $("btnConfirmOk").onclick = () => done(true);
    $("btnConfirmCancel").onclick = () => done(false);
  });
}

// ---- chat history (localStorage) ----
const HISTORY_KEY = "horsir:history:v1";
const CURRENT_SESSION_KEY = "horsir:currentSession:v2";
interface ChatHistoryEntry {
  id: string;
  title: string;
  date: string;
  host?: HostKind;
  chat: ChatMessage[];
}
function loadHistory(): ChatHistoryEntry[] {
  try {
    const raw = window.localStorage.getItem(HISTORY_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}
function saveHistoryList(list: ChatHistoryEntry[]) {
  try { window.localStorage.setItem(HISTORY_KEY, JSON.stringify(list)); } catch { /* ignore */ }
}
/** Auto-save current chat so it survives taskpane close. Host is stored too,
 *  so reopening in a different Office app starts a fresh chat instead of
 *  restoring the other app's conversation. */
function saveCurrentSession() {
  if (!state.chat.length) {
    clearCurrentSession();
    return;
  }
  try { window.localStorage.setItem(CURRENT_SESSION_KEY, JSON.stringify({ host: state.host, chat: state.chat })); } catch { /* ignore */ }
}
function loadCurrentSession(): { host: HostKind | null; chat: ChatMessage[] } | null {
  try {
    const raw = window.localStorage.getItem(CURRENT_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    // v2 stores { host, chat }; the legacy v1 value was a bare ChatMessage[].
    if (Array.isArray(parsed)) return { host: null, chat: parsed as ChatMessage[] };
    if (!parsed || !Array.isArray((parsed as { chat?: unknown }).chat)) return null;
    const host = (parsed as { host?: unknown }).host;
    return { host: typeof host === "string" ? (host as HostKind) : null, chat: (parsed as { chat: ChatMessage[] }).chat };
  } catch { return null; }
}
function clearCurrentSession() {
  try { window.localStorage.removeItem(CURRENT_SESSION_KEY); } catch { /* ignore */ }
}
function saveCurrentChatToHistory() {
  if (!state.chat.length) return;
  const list = loadHistory();
  // Skip if current chat already exists in history (prevents duplicates when
  // switching between restored sessions).
  if (list.some((e) => e.chat.length === state.chat.length && e.chat[0]?.content === state.chat[0]?.content)) return;
  const firstUser = state.chat.find((m) => m.role === "user");
  const title = firstUser ? firstUser.content.slice(0, 40).replace(/\n/g, " ") : "新对话";
  const now = new Date();
  const entry: ChatHistoryEntry = {
    id: `${now.getTime()}-${Math.random().toString(36).slice(2, 6)}`,
    title,
    date: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")} ${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`,
    host: state.host,
    chat: [...state.chat],
  };
  list.unshift(entry);
  // Keep last 20
  if (list.length > 20) list.splice(20);
  saveHistoryList(list);
  clearCurrentSession();
}

function renderHistoryDropdown() {
  const list = loadHistory();
  const container = $("historyList");
  container.innerHTML = "";
  if (!list.length) {
    container.append(h("div", { class: "history-empty" }, t(L.SETTINGS.historyEmpty)));
    return;
  }
  list.forEach((entry) => {
    const item = h("div", { class: "history-item" });
    const body = h("div", { style: "flex:1;overflow:hidden" });
    const titleRow = h("div", { class: "history-item-title" });
    titleRow.append(h("span", { class: "history-item-title-text" }, entry.title));
    if (entry.host && entry.host !== "unknown") {
      titleRow.append(h("span", { class: "history-host-badge" }, hostLabel(entry.host)));
    }
    body.append(
      titleRow,
      h("div", { class: "history-item-date" }, entry.date)
    );
    body.addEventListener("click", () => restoreHistoryEntry(entry));
    item.append(body);
    const delBtn = h("button", { class: "history-del-btn", type: "button" }, "×");
    delBtn.addEventListener("click", async (e) => {
      e.stopPropagation();
      const ok = await showConfirm(t(L.SETTINGS.confirmDeleteMsg));
      if (!ok) return;
      const updated = loadHistory().filter((e) => e.id !== entry.id);
      saveHistoryList(updated);
      closeHistoryDropdown();
      setStatus(t(L.STATUS.saved), "");
    });
    item.append(delBtn);
    container.append(item);
  });
}

function openHistoryDropdown() {
  renderHistoryDropdown();
  $("historyDropdown").hidden = false;
}
function closeHistoryDropdown() {
  $("historyDropdown").hidden = true;
}

function restoreHistoryEntry(entry: ChatHistoryEntry) {
  if (!Array.isArray(entry.chat)) {
    setStatus(t(L.SETTINGS.historyRestoreErr), "error");
    return;
  }
  // Save current chat before overwriting, so user can find it back in history
  saveCurrentChatToHistory();

  state.chat = entry.chat;
  state.lastResult = "";
  setStatus(t(L.SETTINGS.historyRestored));
  closeHistoryDropdown();

  // Re-render the transcript
  const transcript = $("transcript");
  transcript.innerHTML = "";
  chatStarted = true;
  entry.chat.forEach((m) => {
    const turn = h("div", { class: `turn ${m.role}` });
    turn.append(h("span", { class: "role" }, m.role === "user" ? "我" : m.role === "assistant" ? BRAND_NAME : m.role));
    const body = h("div", { class: "turn-body" });
    if (m.role === "assistant") {
      body.innerHTML = renderMarkdown(m.content);
    } else {
      body.textContent = m.content;
    }
    turn.append(body);
    turn.append(makeTurnActions(turn, m));
    transcript.append(turn);
    if (m.role === "user") maybeAddExpandToggle(body);
  });
  scrollTranscript();
  toggleApply(false);
  updateCopyBtn();
  saveCurrentSession();
}

/** On startup, restore the last session if one was auto-saved before taskpane
 *  close — but only when it belongs to the same Office host. A session saved
 *  in another app (e.g. PowerPoint) is archived to history instead, so
 *  switching Word ↔ PPT ↔ Excel always lands on a fresh chat. */
function restoreLastSession() {
  const saved = loadCurrentSession();
  if (!saved || !saved.chat.length) return;

  if (saved.host && saved.host !== state.host) {
    state.chat = saved.chat;
    saveCurrentChatToHistory();
    state.chat = [];
    clearCurrentSession();
    return;
  }

  state.chat = saved.chat;
  state.lastResult = saved.chat.filter((m) => m.role === "assistant").pop()?.content || "";

  const transcript = $("transcript");
  transcript.innerHTML = "";
  chatStarted = true;
  saved.chat.forEach((m) => {
    const turn = h("div", { class: `turn ${m.role}` });
    turn.append(h("span", { class: "role" }, m.role === "user" ? "我" : m.role === "assistant" ? BRAND_NAME : m.role));
    const body = h("div", { class: "turn-body" });
    if (m.role === "assistant") {
      body.innerHTML = renderMarkdown(m.content);
    } else {
      body.textContent = m.content;
    }
    turn.append(body);
    turn.append(makeTurnActions(turn, m));
    transcript.append(turn);
    if (m.role === "user") maybeAddExpandToggle(body);
  });
  scrollTranscript();
  toggleApply(!!state.lastResult);
  updateCopyBtn();
}
// ----------------------------------------------------------------------------
// Boot
// ----------------------------------------------------------------------------
let booted = false;

function bootMsg(text: string) {
  const boot = document.getElementById("boot");
  if (boot) boot.textContent = text;
}

// Surface errors that the dev overlay would hide (e.g. office.js "Script error.").
window.addEventListener("error", (ev) => {
  if (!booted) bootMsg(t(L.BOOT.loadError) + (ev.message || "Script error") + " — 多为 Office 缓存过期，请清缓存后重开侧栏。");
});
window.addEventListener("unhandledrejection", (ev) => {
  if (booted) return;
  const r = (ev as PromiseRejectionEvent).reason as { message?: string } | undefined;
  bootMsg(t(L.BOOT.initRejected) + String((r && (r.message || r)) || "未知错误"));
});
// Watchdog: if neither onReady nor initialize fires, say so instead of hanging forever.
window.setTimeout(() => {
  if (!booted) {
    bootMsg(t(L.BOOT.officeTimeout));
  }
}, 8000);

function startApp(host: HostKind) {
  if (booted) return;

  // Guard: Office.initialize may fire before the DOM is fully parsed (especially
  // on Windows with cached office.js). Retry via rAF until the transcript exists,
  // or wait for DOMContentLoaded if it hasn't fired yet.
  if (!document.getElementById("transcript")) {
    bootMsg(t(L.BOOT.pageLoading));
    if (document.readyState === "loading") {
      window.addEventListener("DOMContentLoaded", () => startApp(host), { once: true });
    } else {
      // DOM already ready but transcript missing — HTML bug, try once more after a tick.
      window.requestAnimationFrame(() => startApp(host));
    }
    return;
  }

  booted = true;
  try {
    setLang(loadLang());
    applyTheme(loadTheme());
    state.host = host;
    // Apply brand name to topbar / document title (single source: src/core/brand.ts).
    const brandEl = document.getElementById("brandName");
    if (brandEl) brandEl.textContent = BRAND_NAME;
    document.title = brandDisplay();
    $("hostBadge").textContent = hostLabel(state.host);

    initSettings();
    applyI18n();
    buildApplyActions();
    buildPanel();

    // Restore last session if it survived a taskpane close
    restoreLastSession();

    $("btnSettings").addEventListener("click", () => openSettings(true));
    $("btnCloseSettings").addEventListener("click", () => openSettings(false));
    $("btnCopy").addEventListener("click", () => copyResult());
    $("btnExportChat").addEventListener("click", () => exportChat());
    $("btnNewChat").addEventListener("click", () => {
      saveCurrentChatToHistory();
      clearChat();
      ($("composerInput") as HTMLTextAreaElement)?.focus();
    });
    $("btnHistory").addEventListener("click", () => openHistoryDropdown());
    $("btnCloseHistory").addEventListener("click", () => closeHistoryDropdown());
    $("btnExportAllHistory").addEventListener("click", () => exportAllHistory());
    // Close dropdown when clicking outside
    document.addEventListener("click", (e) => {
      const tgt = e.target as Node | null;
      if (!$("historyDropdown").hidden && !tgt) return;
      if (!$("historyDropdown").hidden && !$("historyDropdown").contains(tgt) && tgt !== $("btnHistory") && !$("btnHistory").contains(tgt)) {
        closeHistoryDropdown();
      }
    });

    $("boot").hidden = true;
    $("app").hidden = false;

    applyAuthState();
    updateFooter();
    void detectBackend();

    // Auto-refresh NE auth token if it's expired or within 1 hour of expiry.
    void tryAutoRefreshNeAuth();

    if (!isConfigured(state.config) || !state.config.apiKey) openSettings(true);
  } catch (e) {
    const msg = (e as Error)?.message ?? String(e);
    bootMsg(t(L.BOOT.initProblem) + msg);
    const app = document.getElementById("app");
    if (app) app.hidden = false;
  }
}

function hostFromContext(): HostKind {
  try {
    const ctx = (Office as unknown as { context?: { diagnostics?: { host?: Office.HostType } } }).context;
    return detectHost(ctx?.diagnostics?.host ?? null);
  } catch {
    return "unknown";
  }
}

// Modern, supported entry point.
Office.onReady((info) => {
  // 非 Office 环境下 info.host 为 null → 重定向到帮助页（replace 避免后退死循环）。
  if (!info.host) {
    window.location.replace("/help.html");
    return;
  }
  startApp(detectHost(info.host));
});
// Belt-and-suspenders: some hosts / cached office.js call the legacy initialize.
(Office as unknown as { initialize?: (reason?: unknown) => void }).initialize = () => startApp(hostFromContext());

function detectHost(host: Office.HostType | null): HostKind {
  switch (host) {
    case Office.HostType.Word:
      return "word";
    case Office.HostType.Excel:
      return "excel";
    case Office.HostType.PowerPoint:
      return "powerpoint";
    default:
      return "unknown";
  }
}
function hostLabel(host: HostKind): string {
  return { word: "WORD", excel: "EXCEL", powerpoint: "PPT", unknown: "—" }[host];
}

// ----------------------------------------------------------------------------
// Settings drawer
// ----------------------------------------------------------------------------
function initSettings() {
  const presetSel = $("presetSelect") as HTMLSelectElement;
  presetSel.append(h("option", { value: "" }, t(L.SETTINGS.presetCustom)));
  PRESETS.forEach((p, i) => presetSel.append(h("option", { value: String(i) }, p.label)));
  presetSel.addEventListener("change", () => {
    const idx = presetSel.value;
    if (idx === "") return;
    const p = PRESETS[Number(idx)];
    ($("baseUrl") as HTMLInputElement).value = p.baseUrl;
    ($("model") as HTMLInputElement).value = p.model;
    if (p.contextTokens) {
      ($("contextMaxTokens") as HTMLInputElement).value = String(Math.round(p.contextTokens / 1024));
    }
    $("settingsStatus").textContent = p.note ?? "";
  });

  const temp = $("temperature") as HTMLInputElement;
  temp.addEventListener("input", () => ($("tempVal").textContent = temp.value));

  // Context window controls
  const ctxThreshold = $("contextSummaryThreshold") as HTMLInputElement;
  ctxThreshold.addEventListener("input", () => ($("ctxThresholdVal").textContent = ctxThreshold.value + "%"));

  // Footer "确定": persist every field (model + search) then close.
  // Footer "取消" / header "✕": just close without saving.
  const closeSettings = () => openSettings(false);
  const saveAndClose = () => {
    state.customConfig = readSettingsForm();
    saveConfig(state.customConfig);
    state.search = readSearchForm();
    saveSearch(state.search);
    state.contextConfig = readContextForm();
    saveContextConfig(state.contextConfig);
    applyAuthState();
    updateFooter();
    refreshNetToggle();
    setSettingsStatus(t(L.STATUS.saved), "");
    setTimeout(closeSettings, 250);
  };
  $("btnSettingsOk").addEventListener("click", saveAndClose);
  $("btnSettingsCancel").addEventListener("click", closeSettings);
  $("btnTest").addEventListener("click", testConnection);

  initSearchSettings();
  initTheme();
  initLang();
  initNeAccount();
  writeSettingsForm(state.customConfig);
  writeContextForm(state.contextConfig);
}

/** Effective LLM config: NE built-in (via gateway) when chosen, else custom. */
function effectiveConfig(): LLMConfig {
  if (state.ne && state.ne.useBuiltin) return neConfig(state.ne.token, state.customConfig.temperature, state.ne.selectedModel);
  return state.customConfig;
}
/** Recompute the active config from auth + custom settings. */
function applyAuthState() {
  state.config = effectiveConfig();
  updateFooter();
}

// ---- NE account UI ----
function initNeAccount() {
  renderNeAccount();
}

function renderNeAccount() {
  const box = document.getElementById("neAccount");
  if (!box) return;
  box.innerHTML = "";
  const zh = getLang() === "zh";
  box.append(h("span", { class: "eyebrow" }, zh ? "NE 账户" : "NE Account"));

  // Auto-fetch models when logged in with builtin but cache is empty.
  if (state.ne && state.ne.useBuiltin && !neModelCache.length && !neModelFetchFailed) {
    neFetchModels(state.ne.token).then((result) => {
      if (result.ok && result.models && result.models.length) {
        neModelCache = result.models;
        neModelFetchFailed = false;
        if (!state.ne!.selectedModel || !result.models.find((m) => m.id === state.ne!.selectedModel)) {
          state.ne!.selectedModel = result.models[0].id;
          saveNeAuth(state.ne!);
          applyAuthState();
        }
        renderNeAccount();
        updateFooter();
      } else {
        // Token expired — keep login state, show re-login prompt.
        neModelFetchFailed = true;
        renderNeAccount();
        updateFooter();
      }
    }).catch(() => {
      // Network error — keep login state, show re-login prompt.
      neModelFetchFailed = true;
      renderNeAccount();
      updateFooter();
    });
    // Show a brief loading indicator while fetching.
    box.append(h("p", { class: "hint" }, zh ? "正在获取模型列表…" : "Loading models…"));
    return;
  }

  if (!state.ne) {
    const u = h("input", { type: "text", class: "field-input", placeholder: zh ? "NE 账号（邮箱/手机）" : "NE account" }) as HTMLInputElement;
    const p = h("input", { type: "password", class: "field-input", placeholder: zh ? "密码" : "Password", autocomplete: "off" }) as HTMLInputElement;
    // Pre-fill saved credentials
    const savedCred = loadNeCredentials();
    if (savedCred) {
      u.value = savedCred.username;
      p.value = savedCred.password;
    }
    const status = h("p", { class: "hint", id: "neStatus" }, "");
    const loginBtn = h("button", { class: "btn btn-primary btn-sm", type: "button", style: "flex:1" }, zh ? "登录" : "Sign in") as HTMLButtonElement;
    // CARSI 登录暂时隐藏：回调无法正常返回，待修复后恢复 display:flex 与 flex:1
    const carsiBtn = h("button", { class: "btn btn-ghost btn-sm", type: "button", style: "display:none" }, zh ? "CARSI 登录" : "CARSI") as HTMLButtonElement;
    loginBtn.addEventListener("click", async () => {
      if (!u.value.trim() || !p.value) {
        status.textContent = zh ? "请输入账号和密码" : "Enter account and password";
        return;
      }
      loginBtn.disabled = true;
      status.textContent = zh ? "登录中…" : "Signing in…";
      const r = await neAccountLogin(u.value.trim(), p.value);
      loginBtn.disabled = false;
      if (!r.ok) {
        status.textContent = (zh ? "登录失败：" : "Failed: ") + (r.msg || "");
        return;
      }
      // Save credentials for auto-refresh on next launch
      saveNeCredentials(u.value.trim(), p.value);
      onLoggedIn(r.token!, r.user!);
    });
    carsiBtn.addEventListener("click", async () => {
      status.textContent = zh ? "正在打开 CARSI 登录窗口…" : "Opening CARSI…";
      const r = await neCarsiLogin();
      if (!r.ok) {
        status.textContent = "CARSI: " + (r.msg || "");
        return;
      }
      onLoggedIn(r.token!, r.user!);
    });
    box.append(
      h("div", { class: "ne-form" }, u, p),
      h("div", { class: "row", style: "gap:6px" }, loginBtn, carsiBtn),
      status,
      h("p", { class: "hint" }, zh ? "登录后可直接使用 NE 内置模型，无需填写下方接口地址与密钥。" : "After signing in you can use NE's built-in model — no API key needed.")
    );
  } else {
    const user = state.ne.user;
    const info = h("div", { class: "ne-user" });
    if (user.icon_url) info.append(h("img", { class: "ne-avatar", src: user.icon_url, alt: "" }));
    info.append(
      h("div", { class: "ne-user-meta" },
        h("div", { class: "ne-name" }, user.nick_name || user.name || user.email || (zh ? "已登录" : "Signed in")),
        h("div", { class: "hint" }, user.type_txt || "")
      )
    );
    const logout = h("button", { class: "btn btn-ghost btn-sm", type: "button" }, zh ? "退出" : "Sign out") as HTMLButtonElement;
    logout.addEventListener("click", () => {
      clearNeAuth();
      clearNeCredentials();
      state.ne = null;
      neModelCache = [];
      neModelFetchFailed = false;
      applyAuthState();
      renderNeAccount();
      reflectModelSource();
    });
    info.append(logout);

    const builtinBtn = h("button", { class: "swatch", type: "button", "aria-pressed": String(state.ne.useBuiltin) }, zh ? "内置模型(NE)" : "Built-in (NE)") as HTMLButtonElement;
    const customBtn = h("button", { class: "swatch", type: "button", "aria-pressed": String(!state.ne.useBuiltin) }, zh ? "自定义接口" : "Custom API") as HTMLButtonElement;
    builtinBtn.addEventListener("click", () => setModelSource(true));
    customBtn.addEventListener("click", () => setModelSource(false));

    const children: (Node | string)[] = [
      info,
      h("div", { class: "label-sm" }, zh ? "模型来源" : "Model source"),
      h("div", { class: "theme-row" }, builtinBtn, customBtn),
    ];

    // Model picker when using built-in
    if (state.ne.useBuiltin) {
      const modelSel = h("select", { class: "field-input", id: "neModelSelect" }) as HTMLSelectElement;
      const refreshBtn = h("button", { class: "btn btn-ghost btn-sm", type: "button" }, zh ? "刷新列表" : "Refresh") as HTMLButtonElement;

      function populateModels(models: NeModelInfo[]) {
        modelSel.innerHTML = "";
        if (!models.length) {
          modelSel.append(h("option", { value: "" }, state.ne!.selectedModel || (zh ? "（暂无模型）" : "(no models)")));
          return;
        }
        models.forEach((m) => {
          const extra = m.owned_by && m.owned_by !== "configured" ? ` (${m.owned_by})` : "";
          const opt = h("option", { value: m.id }, m.id + extra);
          if (m.id === state.ne!.selectedModel) opt.setAttribute("selected", "");
          modelSel.append(opt);
        });
      }
      populateModels(neModelCache);

      modelSel.addEventListener("change", () => {
        if (state.ne) {
          state.ne.selectedModel = modelSel.value;
          saveNeAuth(state.ne);
          applyAuthState();
          updateFooter();
        }
      });

      refreshBtn.addEventListener("click", async () => {
        refreshBtn.disabled = true;
        refreshBtn.textContent = zh ? "刷新中…" : "Loading…";
        neModelFetchFailed = false;
        const result = await neFetchModels(state.ne!.token);
        refreshBtn.disabled = false;
        refreshBtn.textContent = zh ? "刷新列表" : "Refresh";
        if (result.ok && result.models && result.models.length) {
          neModelCache = result.models;
          neModelFetchFailed = false;
          if (!state.ne!.selectedModel || !result.models.find((m) => m.id === state.ne!.selectedModel)) {
            state.ne!.selectedModel = result.models[0].id;
            saveNeAuth(state.ne!);
            applyAuthState();
          }
          populateModels(neModelCache);
          updateFooter();
        } else {
          neModelFetchFailed = true;
          setSettingsStatus((zh ? "获取模型失败：" : "Failed: ") + (result.msg || ""), "error");
        }
      });

      children.push(
        h("div", { class: "label-sm", style: "margin-top:8px" }, zh ? "选择模型" : "Model"),
        h("div", { class: "row", style: "gap:6px" }, modelSel, refreshBtn),
      );
    }

    if (neModelFetchFailed) {
      const warnBox = h("div", { class: "ne-auth-warn" });
      warnBox.append(
        h("p", { class: "hint", style: "color:var(--color-err, #c8102e);margin:0 0 6px" },
          zh ? "⚠️ 登录状态已失效，请重新登录" : "⚠️ Session expired. Please sign in again.")
      );
      const reloginBtn = h("button", { class: "btn btn-primary btn-sm", type: "button" },
        zh ? "重新登录" : "Re-sign in") as HTMLButtonElement;
      reloginBtn.addEventListener("click", () => {
        clearNeAuth();
        clearNeCredentials();
        state.ne = null;
        neModelCache = [];
        neModelFetchFailed = false;
        applyAuthState();
        renderNeAccount();
        reflectModelSource();
        updateFooter();
      });
      warnBox.append(reloginBtn);
      children.push(warnBox);
    }
    children.push(
      h("p", { class: "hint", id: "neStatus" }, zh ? "内置模型由 NE 账户计费；若额度不足，请前往 NE 账户充值。" : "Built-in model is billed to your NE account.")
    );

    box.append(...children);
  }
  reflectModelSource();
}

async function onLoggedIn(token: string, user: NeUser) {
  const auth: NeAuth = { token, user, useBuiltin: true };
  state.ne = auth;
  saveNeAuth(auth);
  applyAuthState();
  neModelCache = [];
  neModelFetchFailed = false;
  renderNeAccount();
  // Fetch available models from the NE gateway.
  const result = await neFetchModels(token);
  if (result.ok && result.models && result.models.length) {
    neModelCache = result.models;
    neModelFetchFailed = false;
    if (!auth.selectedModel || !result.models.find((m) => m.id === auth.selectedModel)) {
      auth.selectedModel = result.models[0].id;
      state.ne = auth;
      saveNeAuth(auth);
      applyAuthState();
    }
    renderNeAccount();
    updateFooter();
  } else {
    // Token invalid right after login — keep state, show re-login prompt.
    neModelFetchFailed = true;
    renderNeAccount();
    updateFooter();
  }
}

/**
 * Keep the NE session usable without asking the user to re-login every time
 * the server-side session expires. NE tokens are opaque (not JWTs), so the
 * local expiry heuristic is only a fast path — the authoritative check is a
 * cheap /api/ne/models probe. On failure we silently re-login with the saved
 * credentials; only when that's impossible (e.g. CARSI login, changed
 * password) do we surface the re-login UI right away instead of waiting for
 * the next task to fail with a 401.
 */
async function tryAutoRefreshNeAuth(): Promise<void> {
  if (!state.ne || !state.ne.useBuiltin) return;
  if (neModelCache.length) return; // already validated this session

  const zh = getLang() === "zh";
  let expired = isNeTokenExpiring(state.ne);
  if (!expired) {
    const check = await neFetchModels(state.ne.token);
    if (check.ok && check.models?.length) {
      neModelCache = check.models;
      neModelFetchFailed = false;
      adoptDefaultModel(check.models);
      return;
    }
    expired = true; // probe failed → treat session as expired
  }

  const refreshed = await tryRefreshNeAuth(state.ne);
  if (!refreshed) {
    // Can't refresh — surface the re-login prompt now, not at the next task.
    neModelFetchFailed = true;
    renderNeAccount();
    updateFooter();
    setStatus(zh ? "NE 登录状态已失效，请在设置中重新登录" : "NE session expired — please sign in again via Settings", "warn");
    return;
  }

  state.ne = refreshed;
  applyAuthState();
  neModelCache = [];
  neModelFetchFailed = false;

  // Re-fetch model list with the new token
  const result = await neFetchModels(refreshed.token);
  if (result.ok && result.models?.length) {
    neModelCache = result.models;
    adoptDefaultModel(result.models);
  } else {
    neModelFetchFailed = true;
  }

  renderNeAccount();
  updateFooter();
  setStatus(zh ? "NE 登录已自动续期" : "NE session refreshed", "");
}

/** Default-select the first model when the saved selection is missing/invalid. */
function adoptDefaultModel(models: NeModelInfo[]) {
  if (!state.ne) return;
  if (!state.ne.selectedModel || !models.find((m) => m.id === state.ne!.selectedModel)) {
    state.ne.selectedModel = models[0].id;
    saveNeAuth(state.ne);
    applyAuthState();
  }
}

function setModelSource(builtin: boolean) {
  if (state.ne) {
    state.ne.useBuiltin = builtin;
    saveNeAuth(state.ne);
  }
  if (builtin) neModelFetchFailed = false;
  applyAuthState();
  renderNeAccount();
}
function reflectModelSource() {
  const custom = document.getElementById("customModelFields");
  const useBuiltin = !!(state.ne && state.ne.useBuiltin);
  if (custom) (custom as HTMLElement).hidden = useBuiltin;
}

// ---- web search settings ----
function initSearchSettings() {
  const sel = $("searchProvider") as HTMLSelectElement;
  SEARCH_PRESETS.forEach((p) => {
    const label = p.i18nLabelKey && L.SETTINGS[p.i18nLabelKey.split(".")[1] as keyof typeof L.SETTINGS]
      ? t(L.SETTINGS[p.i18nLabelKey.split(".")[1] as keyof typeof L.SETTINGS])
      : p.label;
    sel.append(h("option", { value: p.value }, label));
  });
  sel.value = state.search.provider;
  // Save current inputs under the OLD provider, then load the NEW provider's
  // stored credentials — so each engine keeps its own key/endpoint.
  sel.addEventListener("change", () => {
    const prevProvider = state.search.provider;
    const keyInput = $("searchApiKey") as HTMLInputElement;
    const epInput = $("searchEndpoint") as HTMLInputElement;
    state.search.credentials = putSearchCredential(state.search.credentials, prevProvider, keyInput.value.trim(), epInput.value.trim());
    const nextProvider = sel.value as SearchProvider;
    const cred = getSearchCredential(state.search.credentials, nextProvider);
    keyInput.value = cred.apiKey;
    epInput.value = cred.endpoint;
    state.search.provider = nextProvider;
    reflectSearchFields();
  });
  // Seed inputs from the active provider's stored credentials.
  const cred0 = getSearchCredential(state.search.credentials, state.search.provider);
  ($("searchApiKey") as HTMLInputElement).value = state.search.apiKey || cred0.apiKey;
  ($("searchEndpoint") as HTMLInputElement).value = state.search.endpoint || cred0.endpoint;
  $("btnTestSearch").addEventListener("click", testSearch);
  $("btnExportConfig").addEventListener("click", exportConfig);
  $("btnImportConfig").addEventListener("click", () => ($("importFile") as HTMLInputElement).click());
  ($("importFile") as HTMLInputElement).addEventListener("change", importConfig);
  reflectSearchFields();
}

function reflectSearchFields() {
  const provider = ($("searchProvider") as HTMLSelectElement).value as SearchProvider;
  const preset = SEARCH_PRESETS.find((p) => p.value === provider);
  const note = preset?.i18nNoteKey && L.SETTINGS[preset.i18nNoteKey.split(".")[1] as keyof typeof L.SETTINGS]
    ? t(L.SETTINGS[preset.i18nNoteKey.split(".")[1] as keyof typeof L.SETTINGS])
    : preset?.note ?? "";
  $("searchNote").textContent = note;
  $("searchKeyField").hidden = !preset?.needsKey;
  $("searchEndpointField").hidden = !preset?.needsEndpoint;
  // Guidance link: where to sign up / create the key (or deployment docs).
  const link = $("searchKeyLink") as HTMLAnchorElement;
  const linkWrap = $("searchKeyLinkWrap");
  if (preset?.keyUrl) {
    link.href = preset.keyUrl;
    link.textContent = t(preset.needsEndpoint ? L.SETTINGS.viewDocsLink : L.SETTINGS.getKeyLink);
    linkWrap.hidden = false;
  } else {
    linkWrap.hidden = true;
  }
}

/** Returns true if the current search provider has the required credentials. */
function isSearchUsable(): boolean {
  const preset = SEARCH_PRESETS.find((p) => p.value === state.search.provider);
  if (!preset) return false;
  const cred = getSearchCredential(state.search.credentials, state.search.provider);
  if (preset.needsKey && !cred.apiKey) return false;
  if (preset.needsEndpoint && !cred.endpoint) return false;
  return true;
}

function refreshNetToggle() {
  const net = document.getElementById("btnNet");
  if (net) net.hidden = !isSearchUsable();
}

function readSearchForm(): SearchConfig {
  const provider = ($("searchProvider") as HTMLSelectElement).value as SearchProvider;
  const apiKey = ($("searchApiKey") as HTMLInputElement).value.trim();
  const endpoint = ($("searchEndpoint") as HTMLInputElement).value.trim();
  // Persist the active provider's inputs into the credentials map so save
  // always captures the latest key/endpoint per engine.
  const credentials = putSearchCredential(state.search.credentials, provider, apiKey, endpoint);
  return { provider, apiKey, endpoint, maxResults: state.search.maxResults || 5, credentials };
}

async function testSearch() {
  if (!state.backend) return setSettingsStatus(t(L.STATUS.noBackend), "warn");
  const search = readSearchForm();
  setSettingsStatus(t(L.STATUS.searchTesting), "");
  try {
    const resp = await fetch("/api/search", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ search, query: `${BRAND_NAME} 测试 搜索`, max: 3 }),
    });
    const data = await resp.json();
    if (!resp.ok) throw new Error(data?.error || `错误 ${resp.status}`);
    setSettingsStatus(tf(L.STATUS.searchOk, data?.results?.length ?? 0), "");
  } catch (e) {
    setSettingsStatus(t(L.STATUS.searchFailed) + (e as Error).message, "error");
  }
}

function exportConfig() {
  const payload = {
    version: 2,
    llm: readSettingsForm(),
    search: readSearchForm(),
    context: readContextForm(),
    theme: loadTheme(),
    lang: loadLang(),
  };
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const d = new Date();
  const stamp = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}-${String(d.getHours()).padStart(2, "0")}${String(d.getMinutes()).padStart(2, "0")}`;
  a.download = `${BRAND_SLUG}-config-${stamp}.json`;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
  setSettingsStatus(t(L.SETTINGS.configExported), "");
}

function importConfig() {
  const input = $("importFile") as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;
  const reader = new FileReader();
  reader.onload = () => {
    try {
      const data = JSON.parse(reader.result as string);
      const llm = data?.llm;
      const search = data?.search;
      if (!llm || !search) throw new Error("缺少 llm 或 search 字段");
      ($("baseUrl") as HTMLInputElement).value = llm.baseUrl || "";
      ($("apiKey") as HTMLInputElement).value = llm.apiKey || "";
      ($("model") as HTMLInputElement).value = llm.model || "";
      ($("temperature") as HTMLInputElement).value = String(llm.temperature ?? 0.7);
      $("tempVal").textContent = String(llm.temperature ?? 0.7);
      // Context config (optional in older exports)
      if (data.context) {
        writeContextForm(data.context as ContextConfig);
      }
      // Theme & language (optional, added in v2)
      if (data.theme && THEMES.some((t) => t.value === data.theme)) {
        applyTheme(data.theme as ThemeName);
        try { window.localStorage.setItem(THEME_KEY, data.theme); } catch { /* ignore */ }
      }
      if (data.lang === "zh" || data.lang === "en") {
        setLang(data.lang as Lang);
        saveLang(data.lang as Lang);
        applyI18n();
        buildApplyActions();
        updateFooter();
      }
      ($("searchProvider") as HTMLSelectElement).value = search.provider || state.search.provider;
      // Prefer the imported provider's stored credential; fall back to the
      // legacy flat fields so old exports still import cleanly.
      const importedCreds = search.credentials as { [k: string]: { apiKey: string; endpoint: string } } | undefined;
      const prov = (search.provider || state.search.provider) as SearchProvider;
      const cred = importedCreds?.[prov] ?? { apiKey: search.apiKey || "", endpoint: search.endpoint || "" };
      ($("searchApiKey") as HTMLInputElement).value = cred.apiKey;
      ($("searchEndpoint") as HTMLInputElement).value = cred.endpoint;
      // Merge imported credentials into the in-memory state.
      state.search = {
        ...state.search,
        provider: prov,
        apiKey: cred.apiKey,
        endpoint: cred.endpoint,
        credentials: { ...(importedCreds as Record<SearchProvider, { apiKey: string; endpoint: string }> | undefined) },
      };
      reflectSearchFields();
      refreshNetToggle();
      setSettingsStatus(t(L.SETTINGS.configImported), "");
    } catch (e) {
      setSettingsStatus(t(L.SETTINGS.importFailed) + (e as Error).message, "error");
    }
  };
  reader.readAsText(file);
  input.value = ""; // allow re-importing the same file
}

// ---- backend detection & footer ----
async function detectBackend() {
  let ok = false;
  for (let i = 0; i < 5; i++) {
    const r = await agentHealth();
    if (r.ok) { ok = true; break; }
    if (i < 4) await new Promise((resolve) => setTimeout(resolve, 2000));
  }
  state.backend = ok;
  setBackendAvailable(ok);
  const el = document.getElementById("agentState");
  if (el) el.textContent = ok ? t(L.STATUS.agentConnected) : t(L.STATUS.agentDisconnected);
  document.querySelectorAll<HTMLButtonElement>(".net-toggle").forEach((b) => {
    b.disabled = !ok;
    b.title = ok ? t(L.STATUS.netSearchTitle) : t(L.STATUS.netSearchNoAgent);
  });
  refreshNetToggle();
  updateFooter();
}

function providerLabel(baseUrl: string): string {
  const hit = PRESETS.find((p) => baseUrl.startsWith(p.baseUrl.replace(/\/+$/, "")));
  if (hit) return hit.label;
  try {
    return new URL(baseUrl).hostname;
  } catch {
    return "自定义";
  }
}

function updateFooter() {
  const el = document.getElementById("modelFooter");
  if (!el) return;
  const agent = state.backend ? t(L.FOOTER.agentOk) : t(L.FOOTER.direct);
  if (state.ne && state.ne.useBuiltin) {
    const model = state.config.model || "NE";
    el.textContent = `${getLang() === "zh" ? "模型" : "Model"}：${model} · NE · ${agent}`;
    return;
  }
  const model = state.config.model || t(L.FOOTER.noModel);
  const prov = state.config.baseUrl ? providerLabel(state.config.baseUrl) : "—";
  el.textContent = `模型：${model} · ${prov} · ${agent}`;
}

// ---- themes (persisted to localStorage) ----
type ThemeName = "default" | "blue" | "anthropic" | "govred";
const THEMES: { value: ThemeName; i18nKey: string; dot: string }[] = [
  { value: "default", i18nKey: "settings.themeDefault", dot: "#0f6e5a" },
  { value: "blue", i18nKey: "settings.themeBlue", dot: "#2563eb" },
  { value: "anthropic", i18nKey: "settings.themeOrange", dot: "#c8633b" },
  { value: "govred", i18nKey: "settings.themeRed", dot: "#c8102e" },
];
const THEME_KEY = "horsir:theme:v1";

function loadTheme(): ThemeName {
  try {
    const v = window.localStorage.getItem(THEME_KEY) as ThemeName | null;
    return v && THEMES.some((t) => t.value === v) ? v : "default";
  } catch {
    return "default";
  }
}
function applyTheme(theme: ThemeName) {
  document.documentElement.setAttribute("data-theme", theme);
}
function initTheme() {
  const current = loadTheme();
  applyTheme(current);
  const row = document.getElementById("themeRow");
  if (!row) return;
  row.innerHTML = "";
  THEMES.forEach((theme) => {
    const btn = h("button", { class: "swatch", type: "button", "aria-pressed": String(theme.value === current) }) as HTMLButtonElement;
    const dot = h("span", { class: "dot" });
    dot.style.background = theme.dot;
    const i18nKey = theme.i18nKey.split(".")[1] as keyof typeof L.SETTINGS;
    btn.append(dot, document.createTextNode(t(L.SETTINGS[i18nKey])));
    btn.addEventListener("click", () => {
      applyTheme(theme.value);
      try {
        window.localStorage.setItem(THEME_KEY, theme.value);
      } catch {
        /* ignore */
      }
      row.querySelectorAll(".swatch").forEach((s) => s.setAttribute("aria-pressed", String(s === btn)));
    });
    row.append(btn);
  });
}

const LANG_KEY = "horsir:lang:v1";
function loadLang(): Lang {
  try {
    const v = window.localStorage.getItem(LANG_KEY) as Lang | null;
    return v === "en" ? "en" : "zh";
  } catch {
    return "zh";
  }
}
function saveLang(lang: Lang) {
  try { window.localStorage.setItem(LANG_KEY, lang); } catch { /* ignore */ }
}
function initLang() {
  const current = getLang();
  const row = document.getElementById("langRow");
  if (!row) return;
  row.innerHTML = "";
  const langs: { value: Lang; label: string }[] = [
    { value: "zh", label: "中文" },
    { value: "en", label: "English" },
  ];
  langs.forEach((l) => {
    const btn = h("button", { class: "swatch", type: "button", "aria-pressed": String(l.value === current) }) as HTMLButtonElement;
    btn.textContent = l.label;
    btn.addEventListener("click", () => {
      setLang(l.value);
      saveLang(l.value);
      row.querySelectorAll(".swatch").forEach((s) => s.setAttribute("aria-pressed", String(s === btn)));
      applyI18n();
      // Rebuild language-dependent UI
      buildApplyActions();
      clearChat();
      updateFooter();
      detectBackend();
    });
    row.append(btn);
  });
}

function readSettingsForm(): LLMConfig {
  return {
    baseUrl: ($("baseUrl") as HTMLInputElement).value.trim(),
    apiKey: ($("apiKey") as HTMLInputElement).value.trim(),
    model: ($("model") as HTMLInputElement).value.trim(),
    temperature: Number(($("temperature") as HTMLInputElement).value),
  };
}
function writeSettingsForm(cfg: LLMConfig) {
  ($("baseUrl") as HTMLInputElement).value = cfg.baseUrl;
  ($("apiKey") as HTMLInputElement).value = cfg.apiKey;
  ($("model") as HTMLInputElement).value = cfg.model;
  ($("temperature") as HTMLInputElement).value = String(cfg.temperature);
  $("tempVal").textContent = String(cfg.temperature);
}
function openSettings(open: boolean) {
  if (open) {
    // Re-render NE account in case a previous fetch failed and left stale UI.
    renderNeAccount();
  }
  $("settingsDrawer").hidden = !open;
}
function readContextForm(): ContextConfig {
  return {
    maxTokens: (Number(($("contextMaxTokens") as HTMLInputElement).value) || 64) * 1024,
    summaryThreshold: (Number(($("contextSummaryThreshold") as HTMLInputElement).value) || 80) / 100,
  };
}
function writeContextForm(cfg: ContextConfig) {
  ($("contextMaxTokens") as HTMLInputElement).value = String(Math.round((cfg.maxTokens || 65536) / 1024));
  const pct = Math.round((cfg.summaryThreshold || 0.8) * 100);
  ($("contextSummaryThreshold") as HTMLInputElement).value = String(pct);
  ($("ctxThresholdVal") as HTMLElement).textContent = pct + "%";
}
function setSettingsStatus(msg: string, kind: "" | "error" | "warn") {
  const el = $("settingsStatus");
  el.textContent = msg;
  el.className = `status-line ${kind}`;
}

async function testConnection() {
  const cfg = readSettingsForm();
  if (!isConfigured(cfg)) return setSettingsStatus(t(L.STATUS.fillUrlModel), "error");
  setSettingsStatus(t(L.STATUS.testConnecting), "");
  try {
    const reply = await chat(cfg, [{ role: "user", content: "ping，请只回复 ok" }], { stream: false });
    // 测试只验证连通性，不自动保存或关闭 modal —— 用户点 footer 的「确定」时统一保存。
    setSettingsStatus(t(L.STATUS.testOk) + reply.slice(0, 40), "");
  } catch (e) {
    setSettingsStatus(t(L.STATUS.testFailed) + (e as Error).message, "error");
  }
}

// ----------------------------------------------------------------------------
// Apply actions (host-specific, act on state.lastResult)
// ----------------------------------------------------------------------------
let applyButtons: HTMLButtonElement[] = [];
function toggleApply(enabled: boolean) {
  applyButtons.forEach((b) => (b.disabled = !enabled || !state.lastResult));
  const la = document.getElementById("lastActions");
  if (la) la.hidden = !enabled;
}
function buildApplyActions() {
  const wrap = $("outputActions");
  wrap.innerHTML = "";
  applyButtons = [];
  const add = (label: string, fn: () => Promise<void>) => {
    const btn = h("button", { class: "btn btn-sm", disabled: "" }, label) as HTMLButtonElement;
    btn.addEventListener("click", () => guardedApply(fn));
    wrap.append(btn);
    applyButtons.push(btn);
  };

  if (state.host === "word") {
    add(t(L.ACTIONS.replaceSel), async () => {
      const c = await cleanForInsert();
      if (c.kind === "word") await Word.replaceSelection(c.text);
    });
    add(t(L.ACTIONS.insertAfter), async () => {
      const c = await cleanForInsert();
      if (c.kind === "word") await Word.insertAfterSelection(c.text);
    });
    add(t(L.ACTIONS.appendEnd), async () => {
      const c = await cleanForInsert();
      if (c.kind === "word") await Word.appendToDocument(c.text);
    });
    if (Word.supportsComments()) {
      add(t(L.ACTIONS.addComment), async () => {
        const c = await cleanForInsert();
        if (c.kind === "word") await Word.addComment(c.text);
      });
    }
  } else if (state.host === "excel") {
    add(t(L.ACTIONS.writeAnalysis), async () => {
      const c = await cleanForInsert();
      if (c.kind === "excel") {
        if (!c.paragraphs.length) throw new Error("没有可写入的内容");
        const name = await Excel.writeSummary("AI 结果", c.paragraphs);
        setStatus(tf(L.ACTIONS.wroteSheet, name));
      }
    });
  } else if (state.host === "powerpoint") {
    add(t(L.ACTIONS.replaceSelPpt), async () => {
      const c = await cleanForInsert();
      if (c.kind === "powerpoint") {
        const text = [c.title, ...c.bullets].filter(Boolean).join("\n");
        await Ppt.setSelectedText(text);
      }
    });
    if (Ppt.supportsAddSlide()) {
      add(t(L.ACTIONS.insertSlide), async () => {
        const c = await cleanForInsert();
        if (c.kind === "powerpoint") {
          const n = await Ppt.addSlide(c.title, c.bullets);
          setStatus(tf(L.ACTIONS.slideInserted, n, c.title));
        }
      });
    }
  }
}

/**
 * Ask the LLM to strip Markdown decorators and conversational filler from the
 * latest chat reply, returning host-native content ready to insert. Used ONLY
 * by the apply-actions toolbar (insert / replace / append / slide) — the
 * normal chat flow is untouched. Falls back to the raw lastResult if the
 * cleaning call fails (e.g. network error), so the action still works.
 */
type CleanInsert =
  | { kind: "word"; text: string }
  | { kind: "excel"; paragraphs: string[] }
  | { kind: "powerpoint"; title: string; bullets: string[] };

async function cleanForInsert(): Promise<CleanInsert> {
  const host = state.host;
  const source = state.lastResult;
  if (host !== "word" && host !== "excel" && host !== "powerpoint") {
    return { kind: "word", text: source };
  }
  if (!source.trim()) {
    if (host === "word") return { kind: "word", text: "" };
    if (host === "excel") return { kind: "excel", paragraphs: [] };
    return { kind: "powerpoint", title: "", bullets: [] };
  }
  setStatus(t(L.STATUS.cleaning));
  const wantJson = host !== "word";
  let raw: string;
  try {
    raw = await chat(state.config, cleanForInsertMessages(host, source), { json: wantJson });
  } catch (e) {
    // Cleaning failed — degrade gracefully and apply the raw reply as before.
    setStatus((e as Error).message, "warn");
    if (host === "word") return { kind: "word", text: source };
    if (host === "excel") return { kind: "excel", paragraphs: source.split(/\n+/).map((s) => s.trim()).filter(Boolean) };
    const p = parseSlide(source);
    return { kind: "powerpoint", title: p.title, bullets: p.bullets };
  }
  if (host === "word") {
    const text = (raw || "").trim();
    return { kind: "word", text };
  }
  const data = parseJsonLenient(raw) as { paragraphs?: unknown[]; title?: unknown; bullets?: unknown[] } | null;
  if (host === "excel") {
    const paragraphs = Array.isArray(data?.paragraphs)
      ? data!.paragraphs.map((x) => String(x ?? "").replace(/\r?\n/g, " ").trim()).filter(Boolean)
      : [];
    return { kind: "excel", paragraphs };
  }
  // powerpoint
  const title = String(data?.title ?? "").trim() || t(L.ACTIONS.newSlideDefault);
  const bullets = Array.isArray(data?.bullets)
    ? data!.bullets.map((x) => String(x ?? "").replace(/\r?\n/g, " ").trim()).filter(Boolean)
    : [];
  return { kind: "powerpoint", title, bullets: bullets.length ? bullets : [title] };
}

/** Best-effort JSON extraction: strips accidental ```json fences and any
 *  trailing prose, then parses the outermost {...} block. Returns null on
 *  failure so callers can fall back. */
function parseJsonLenient(raw: string): unknown {
  if (!raw) return null;
  let s = raw.trim();
  const fence = s.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (fence) s = fence[1].trim();
  const start = s.indexOf("{");
  const end = s.lastIndexOf("}");
  if (start >= 0 && end > start) s = s.slice(start, end + 1);
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

/** Turn a free-form reply into a slide: first line = title, remaining lines = bullets.
 *  Kept as a last-resort fallback when the LLM cleaning call fails. */
function parseSlide(text: string): { title: string; bullets: string[] } {
  const lines = text
    .split(/\n+/)
    .map((l) => l.trim().replace(/^#{1,6}\s*/, "").replace(/^[-*•]\s*/, "").trim())
    .filter(Boolean);
  const title = lines[0] || t(L.ACTIONS.newSlideDefault);
  const bullets = lines.slice(1);
  return { title, bullets: bullets.length ? bullets : [title] };
}
async function guardedApply(fn: () => Promise<void>) {
  try {
    setStatus(t(L.ACTIONS.writing));
    await fn();
    setStatus(t(L.ACTIONS.applied));
  } catch (e) {
    setStatus((e as Error).message, "error");
  }
}

// ----------------------------------------------------------------------------
// Panels
// ----------------------------------------------------------------------------
function buildPanel() {
  const extra = $("extraPanel");
  extra.innerHTML = "";
  if (state.host !== "word" && state.host !== "excel" && state.host !== "powerpoint")
    extra.append(h("div", { class: "card" }, t(L.ACTIONS.unsupportedHostPanel)));
  initComposer();
  clearChat();
}

// ---- transcript helpers ----
let chatStarted = false;
function ensureTranscriptStarted() {
  if (chatStarted) return;
  $("transcript").innerHTML = "";
  chatStarted = true;
}
function scrollTranscript() {
  const t = $("transcript");
  t.scrollTop = t.scrollHeight;
}
/** Decide what document context to attach by default: selection if any, else the whole file. */
async function defaultSource(host: HostKind): Promise<string> {
  try {
    if (host === "word") {
      const sel = await Word.getSelectedText();
      return sel.trim() ? "selection" : "document";
    }
    if (host === "excel") {
      const d = await Excel.getSelectedData();
      return d.headers.length || d.rows.length ? "selection" : "sheet";
    }
    if (host === "powerpoint") {
      const sel = await Ppt.getSelectedText().catch(() => "");
      return sel.trim() ? "selection" : "all";
    }
  } catch {
    /* ignore — fall back to none */
  }
  return "none";
}

/** Wire up the single composer (textarea + 联网 toggle + send). */
function initComposer() {
  const input = $("composerInput") as HTMLTextAreaElement;
  const send = $("btnSend") as HTMLButtonElement;
  const net = $("btnNet") as HTMLButtonElement;

  net.disabled = !state.backend;
  net.title = state.backend ? t(L.STATUS.netSearchTitle) : t(L.STATUS.netSearchNoAgent);
  refreshNetToggle();
  net.setAttribute("aria-pressed", "false");
  net.addEventListener("click", () => {
    const on = net.getAttribute("aria-pressed") === "true";
    net.setAttribute("aria-pressed", String(!on));
  });
  const useSearch = () => net.getAttribute("aria-pressed") === "true";

  function setSending(sending: boolean) {
    input.disabled = sending;
    send.textContent = sending ? "停止" : "发送";
    send.classList.toggle("btn-stop", sending);
    send.classList.toggle("btn-primary", !sending);
  }

  send.addEventListener("click", () => {
    if (state.busy) {
      state.abortController?.abort();
      return;
    }
    setSending(true);
    doSend(input, useSearch()).finally(() => setSending(false));
  });
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      if (!state.busy) {
        setSending(true);
        doSend(input, useSearch()).finally(() => setSending(false));
      }
    }
  });
}

/** Empty-state welcome card with a few quick-start suggestions. */
function welcomeConfigFor(host: HostKind) {
  const wc = L.WELCOME[host as "word" | "excel" | "powerpoint"];
  if (!wc) return null;
  return {
    emoji: wc.emoji,
    greet: t(wc.greet),
    sub: t(wc.sub),
    caps: wc.caps[getLang()],
    groups: wc.groups.map((g) => ({ label: t(g.label), questions: g.questions[getLang()] })),
  };
}

function renderWelcome() {
  const transcript = $("transcript");
  transcript.innerHTML = "";
  chatStarted = false;
  const cfg = welcomeConfigFor(state.host);
  const wrap = h("div", { class: "welcome" });

  if (!cfg) {
    wrap.append(
      h("div", { class: "welcome-greet" }, h("div", { class: "welcome-emoji" }, "👋")),
      h("div", { class: "welcome-sub" }, t(L.WELCOME_UNSUPPORTED))
    );
    transcript.append(wrap);
    return;
  }

  // Greeting block
  const greet = h("div", { class: "welcome-greet" });
  greet.append(
    h("div", { class: "welcome-emoji" }, cfg.emoji),
    h("div", { class: "welcome-title" }, cfg.greet),
    h("div", { class: "welcome-sub" }, cfg.sub)
  );
  wrap.append(greet);

  // Capability bullets (grounded in real Office.js abilities)
  if (cfg.caps.length) {
    const caps = h("div", { class: "welcome-caps" });
    cfg.caps.forEach((c) => caps.append(h("div", { class: "welcome-cap" }, c)));
    wrap.append(caps);
  }

  // Example questions grouped by scenario — click to send immediately
  const examples = h("div", { class: "welcome-examples" });
  cfg.groups.forEach((g) => {
    const group = h("div", { class: "welcome-group" });
    group.append(h("div", { class: "welcome-group-label" }, g.label));
    const chips = h("div", { class: "welcome-chips" });
    g.questions.forEach((q) => {
      const chip = h("button", { class: "chip welcome-chip", type: "button" }, q);
      chip.addEventListener("click", () => {
        const inp = $("composerInput") as HTMLTextAreaElement;
        inp.value = q;
        inp.focus();
      });
      chips.append(chip);
    });
    group.append(chips);
    examples.append(group);
  });
  wrap.append(examples);

  transcript.append(wrap);
}

// ---- Conversational panel (Word & PowerPoint) ----
// ---- Conversational panel: chatbot composer shared by all hosts ----

function chatSystemPrompt(host: HostKind): string {
  const hostKey = host as keyof typeof L.SYS_PROMPTS;
  const base = L.SYS_PROMPTS[hostKey] || L.SYS_PROMPTS.unknown;
  return base + "\n\n" + L.SYS_PROMPTS.toolsIntro;
}

/** Real-time environment given to the model: date/time, host, document name, author. */
async function buildRuntimeContext(host: HostKind): Promise<string> {
  const now = new Date();
  let tz = "";
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    tz = "";
  }
  const dt = now.toLocaleString("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  let docName = "";
  try {
    const url = Office.context?.document?.url || "";
    if (url) docName = decodeURIComponent(url.split(/[\\/]/).pop() || "");
  } catch {
    docName = "";
  }

  let author = "";
  try {
    if (host === "word") author = await Word.getAuthor();
    else if (host === "excel") author = await Excel.getAuthor();
  } catch {
    author = "";
  }

  const parts = [`当前日期时间：${dt}${tz ? `（${tz}）` : ""}`, `Office 宿主：${hostLabel(host)}`];
  if (docName) parts.push(`文档名：${docName}`);
  if (author) parts.push(`作者：${author}`);

  return (
    `[运行环境]\n${parts.join("；")}。\n` +
    "进行网络搜索或回答时效性问题时，请以上述“当前日期时间”为准，使用最新的年份与时间范围，不要默认沿用训练数据里的旧日期。"
  );
}

function sourceLabel(host: HostKind, source: string): string {
  if (source === "none") return "";
  if (host === "word") return source === "document" ? t(L.SOURCE.document) : t(L.SOURCE.selectionWord);
  if (host === "excel") return source === "sheet" ? t(L.SOURCE.sheet) : t(L.SOURCE.selectionSheet);
  return source === "all" ? t(L.SOURCE.allSlides) : t(L.SOURCE.selectionWord);
}

function sheetDataToText(d: { sheetName: string; headers: string[]; rows: unknown[][] }): string {
  if (!d.headers.length && !d.rows.length) return "";
  const head = d.headers.length ? d.headers.join(" | ") : "";
  const body = d.rows
    .slice(0, 50)
    .map((r) => r.join(" | "))
    .join("\n");
  return [`工作表：${d.sheetName}`, head, body].filter(Boolean).join("\n");
}

async function getContextText(host: HostKind, source: string): Promise<string> {
  if (source === "none") return "";
  if (host === "word") {
    return source === "document" ? Word.getDocumentText() : Word.getSelectedText();
  }
  if (host === "excel") {
    const d = source === "sheet" ? await Excel.getUsedRangeData() : await Excel.getSelectedData();
    return sheetDataToText(d);
  }
  if (source === "all") {
    const slides = await Ppt.getAllSlidesText();
    return slides.map((s) => `第 ${s.slide} 页\n${s.texts.join("\n")}`).join("\n\n");
  }
  return Ppt.getSelectedText().catch(() => "");
}

/** If a user turn-body overflows its max-height, add an expand/collapse toggle. */
function maybeAddExpandToggle(body: HTMLElement) {
  requestAnimationFrame(() => {
    if (body.scrollHeight <= body.clientHeight + 4) return;
    const btn = document.createElement("button");
    btn.className = "turn-expand-btn";
    btn.textContent = "▾";
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      const expanded = body.classList.toggle("is-expanded");
      btn.textContent = expanded ? "▴" : "▾";
    });
    body.insertAdjacentElement("afterend", btn);
  });
}

/** Append a turn to the transcript; returns the body element (for streaming updates). */
function appendTurn(role: "user" | "assistant", text: string, msg?: ChatMessage): HTMLElement {
  ensureTranscriptStarted();
  const turn = h("div", { class: `turn ${role}` });
  turn.append(h("span", { class: "role" }, role === "user" ? "我" : BRAND_NAME));
  const body = h("div", { class: "turn-body" });
  body.textContent = text;
  turn.append(body);
  if (msg) turn.append(makeTurnActions(turn, msg));
  $("transcript").append(turn);
  if (role === "user") maybeAddExpandToggle(body);
  scrollTranscript();
  return body;
}

/** Build the per-message action toolbar (copy + delete), revealed on hover.
 *  Uses a closure over the ChatMessage reference so it stays correct even
 *  if the surrounding indices shift after earlier deletions. */
function makeTurnActions(turn: HTMLElement, msg: ChatMessage): HTMLElement {
  const bar = h("div", { class: "turn-actions" });

  // Copy button — copies the raw message source (markdown for assistant).
  const copyBtn = h("button", {
    class: "turn-act-btn",
    type: "button",
    "aria-label": "复制",
    title: "复制",
  }, "⧉");
  copyBtn.addEventListener("click", async (e) => {
    e.stopPropagation();
    const text = msg.content ?? "";
    if (!text) return;
    const ok = await writeClipboard(text);
    if (ok) {
      copyBtn.textContent = "✓";
      copyBtn.classList.add("is-ok");
      window.setTimeout(() => {
        copyBtn.textContent = "⧉";
        copyBtn.classList.remove("is-ok");
      }, 1200);
    }
  });
  bar.append(copyBtn);

  // Resend button — only on user messages. Refills the composer with the
  // original instruction and focuses it, so the user can re-send as-is
  // (press Enter) or tweak before re-sending.
  if (msg.role === "user") {
    const resendBtn = h("button", {
      class: "turn-act-btn",
      type: "button",
      "aria-label": "重新发送",
      title: "重新发送",
    }, "↻");
    resendBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      if (state.busy) return;
      const composer = $("composerInput") as HTMLTextAreaElement | null;
      if (!composer) return;
      composer.value = msg.content ?? "";
      composer.focus();
      // Place the cursor at the end so the user can immediately append/edit.
      const len = composer.value.length;
      composer.setSelectionRange(len, len);
      composer.scrollTop = composer.scrollHeight;
    });
    bar.append(resendBtn);
  }

  // Delete button — confirm, then remove from state + DOM.
  const delBtn = h("button", {
    class: "turn-act-btn is-danger",
    type: "button",
    "aria-label": "删除",
    title: "删除",
  }, "×");
  delBtn.addEventListener("click", async (e) => {
    e.stopPropagation();
    if (state.busy) return;
    const ok = await showConfirm(t(L.SETTINGS.confirmDeleteMsg));
    if (!ok) return;
    const idx = state.chat.indexOf(msg);
    if (idx >= 0) state.chat.splice(idx, 1);
    turn.remove();
    saveCurrentSession();
    const last = state.chat[state.chat.length - 1];
    state.lastResult = last && last.role === "assistant" ? last.content : "";
    updateCopyBtn();
    setStatus(t(L.STATUS.saved), "");
  });
  bar.append(delBtn);
  return bar;
}

/** Keep only the most recent rounds when sending to the model (token control). */
const HISTORY_LIMIT = 12; // ≈ last 6 user/assistant rounds
function historyTail(): ChatMessage[] {
  return state.chat.slice(-HISTORY_LIMIT);
}

function clearChat() {
  state.chat = [];
  state.lastResult = "";
  renderWelcome();
  toggleApply(false);
  updateCopyBtn();
  setStatus("");
}

async function doSend(input: HTMLTextAreaElement, webSearch: boolean) {
  const host = state.host;
  const instruction = input.value.trim();
  if (!instruction) return setStatus(t(L.STATUS.enterMsg), "warn");
  if (!isConfigured(state.config)) {
    openSettings(true);
    setSettingsStatus(t(L.STATUS.notConfigured), "warn");
    return;
  }
  if (state.busy) return;

  state.abortController = new AbortController();
  const signal = state.abortController.signal;

  state.busy = true;
  setStatus(webSearch ? t(L.STATUS.netSearching) : t(L.STATUS.thinking));
  toggleApply(false);

  // Default scope: selection if any, else the current file (no manual picker).
  // NOTE: with the agent flow the model can also call read tools itself, so
  // attaching context here is a convenience, not the only source.
  let source = "none";
  try {
    source = await defaultSource(host);
  } catch {
    source = "none";
  }
  let ctx = "";
  try {
    ctx = await getContextText(host, source);
  } catch {
    ctx = "";
  }
  const label = sourceLabel(host, source);
  const hasCtx = ctx.trim().length > 0;
  // The context excerpt (——— 全文（节选） ——— …) is attached only for the
  // outgoing LLM call this turn. It must NOT be persisted into state.chat
  // (would bloat localStorage + show up in copy / re-renders), and must NOT
  // be echoed in the transcript. state.chat keeps just the raw instruction.
  const excerptSuffix = hasCtx && label
    ? `\n\n——— ${label}（节选） ———\n${ctx.slice(0, 4000)}`
    : "";

  state.chat.push({ role: "user", content: instruction });
  saveCurrentSession();
  appendTurn("user", hasCtx && label ? `${instruction}\n（已附带：${label}）` : instruction, state.chat[state.chat.length - 1]);
  input.value = "";

  const runtimeCtx = await buildRuntimeContext(host);
  const body = appendTurn("assistant", "");
  // Build the message list from historyTail (raw instructions only), then
  // attach this turn's excerpt to the LAST user message — that's the one the
  // model is about to see. History rounds keep plain instructions, so the
  // conversation memory stays compact.
  const tail = historyTail();
  if (hasCtx && tail.length) {
    const last = tail[tail.length - 1];
    tail[tail.length - 1] = { ...last, content: last.content + excerptSuffix };
  }
  const messages: ChatMessage[] = [
    { role: "system", content: `${chatSystemPrompt(host)}\n\n${runtimeCtx}` },
    ...tail,
  ];

  // Render the finished turn into the assistant body + persist it.
  const finishTurn = (text: string, traceHtml: string) => {
    body.classList.add("md-body");
    body.innerHTML = (traceHtml || "") + renderMarkdown(text);
    state.chat.push({ role: "assistant", content: text });
    saveCurrentSession();
    // Attach per-message actions (copy + delete) to the assistant turn now that it's complete.
    const turn = body.closest(".turn");
    if (turn) turn.append(makeTurnActions(turn as HTMLElement, state.chat[state.chat.length - 1]));
    state.lastResult = text;
    scrollTranscript();
    setStatus(t(L.STATUS.done));
    toggleApply(true);
    updateCopyBtn();
  };
  // One LLM attempt (agent loop or plain chat). Reads state.config at call
  // time so a token refresh + retry picks up the new credentials.
  const runTurn = async (): Promise<{ text: string; traceHtml: string }> => {
    if (state.backend) {
      // Unified agent loop: the model decides whether to chat / read / edit / search.
      // Client tools run here in the add-in; the loop resumes until a final answer.
      const render = (all: AgentTraceItem[]) => {
        const html = renderTrace(all);
        if (html) {
          body.classList.add("md-body");
          body.innerHTML = html;
        }
        scrollTranscript();
      };
      const res = await runAgentLoop(body, messages, host, webSearch, render, signal);
      return { text: res.content, traceHtml: renderTrace(res.trace) };
    }
    // No backend → plain streaming chat (no tools available).
    const text = await chat(state.config, messages, {
      stream: true,
      signal,
      onToken: (_d, full) => {
        body.textContent = full;
        scrollTranscript();
      },
    });
    return { text, traceHtml: "" };
  };

  try {
    const { text, traceHtml } = await runTurn();
    finishTurn(text, traceHtml);
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") {
      body.textContent = t(L.STATUS.cancelled);
      setStatus(t(L.STATUS.userCancelled), "");
    } else {
      const raw = (e as Error).message;
      const builtin = !!(state.ne && state.ne.useBuiltin);
      // Detect expired NE token (401 / Unauthorized / Invalid API key).
      const isAuthErr = /401|unauthorized|invalid.*api.?key/i.test(raw);
      let errMsg = raw;
      // NE session expired mid-task → silently re-login with the saved
      // credentials and retry once, instead of bouncing the user to Settings.
      if (builtin && isAuthErr && state.ne) {
        const zh = getLang() === "zh";
        const refreshed = await tryRefreshNeAuth(state.ne);
        if (refreshed) {
          state.ne = refreshed;
          applyAuthState();
          neModelCache = [];
          neModelFetchFailed = false;
          setStatus(zh ? "NE 登录已自动续期，正在重试…" : "NE session refreshed, retrying…", "");
          try {
            const { text, traceHtml } = await runTurn();
            finishTurn(text, traceHtml);
            return;
          } catch (e2) {
            errMsg = (e2 as Error).message;
          }
        }
      }
      let hint = "";
      if (builtin && isQuotaError(errMsg)) {
        hint = getLang() === "zh"
          ? "（NE 内置模型额度不足，请前往 NE 账户充值后重试）"
          : "(NE built-in quota exhausted — please recharge your NE account)";
      } else if (builtin && isAuthErr) {
        hint = getLang() === "zh"
          ? "（NE 登录状态已失效，请在设置中重新登录）"
          : "(NE session expired — please re-sign in via Settings)";
      }
      body.textContent = t(L.STATUS.errorPrefix) + errMsg + hint;
      setStatus(hint ? errMsg + hint : errMsg, "error");
    }
  } finally {
    state.busy = false;
    state.abortController = null;
  }
}

// ----------------------------------------------------------------------------
// Unified agent driver: runs the backend loop, executes client tools locally,
// and resumes until a final answer is produced. Replaces the old split between
// "send" (chat) and "execute" (applyOps) — the agent now decides which to do.
// ----------------------------------------------------------------------------
const MAX_AGENT_ROUNDS = 6;

/** Drive the full agent loop, executing client tools in the add-in. */
async function runAgentLoop(
  body: HTMLElement,
  messages: ChatMessage[],
  host: HostKind,
  webSearch: boolean,
  onTrace: (all: AgentTraceItem[]) => void,
  signal: AbortSignal
): Promise<{ content: string; trace: AgentTraceItem[]; messages: ChatMessage[] }> {
  const allTrace: AgentTraceItem[] = [];
  let convo = [...messages];

  for (let round = 0; round < MAX_AGENT_ROUNDS; round++) {
    signal.throwIfAborted();
    const res = await runAgent({ llm: state.config, search: state.search, host, webSearch, messages: convo, signal, contextConfig: state.contextConfig });
    convo = res.messages;
    allTrace.push(...res.trace);
    onTrace(allTrace);

    if (res.done) {
      return { content: res.content, trace: allTrace, messages: convo };
    }

    // Pending client tools — run them here and feed results back.
    if (res.pending.length) {
      body.textContent = tf(L.STATUS.executing, res.pending.map((p) => TOOL_LABELS[p.name] || p.name).join("、"));
      for (const call of res.pending) {
        signal.throwIfAborted();
        const result = await executeClientTool(call.name, call.args);
        allTrace.push({
          tool: call.name,
          args: call.args,
          ok: result.ok,
          summary: result.ok ? result.summary : `失败：${result.error}`,
        });
        onTrace(allTrace);
        convo.push({
          role: "tool",
          tool_call_id: call.id,
          content: JSON.stringify({ ok: result.ok, summary: result.summary, ...(result.data ? { data: result.data } : {}), ...(result.error ? { error: result.error } : {}) }).slice(0, 6000),
        });
      }
      // Loop continues: send the updated convo back to the backend for the next step.
    }
  }

  // Out of rounds — do one final tool-free completion to wrap up.
  signal.throwIfAborted();
  const final = await runAgent({ llm: state.config, search: state.search, host, webSearch: false, messages: convo, signal, contextConfig: state.contextConfig });
  return { content: final.content, trace: allTrace, messages: final.messages };
}

function renderTrace(trace: AgentTraceItem[]): string {
  if (!trace || !trace.length) return "";
  const lines = trace
    .map((t) => {
      const label = TOOL_LABELS[t.tool] || t.tool;
      const q = t.args && typeof t.args.query === "string" ? `：${escapeHtml(t.args.query)}` : "";
      let mark = "✓";
      if (t.pending) mark = "…";
      else if (t.ok === false) mark = "⚠️";
      else if (t.tool === "web_search") mark = "🔎";
      return `${mark} ${escapeHtml(label)}${q}（${escapeHtml(t.summary || "")}）`;
    })
    .join("<br />");
  return `<div class="trace">${lines}</div>`;
}

function exportChat() {
  if (!state.chat.length) return setStatus(t(L.EXPORT.noChatToExport), "warn");
  const date = new Date();
  const head = t(L.EXPORT.chatTitle) + "\n\n" + tf(L.EXPORT.metaLine, state.config.model, date.toLocaleString()) + "\n";
  const body = state.chat
    .map((m) => (m.role === "user" ? `${t(L.EXPORT.userLabel)}\n\n${m.content}` : m.role === "assistant" ? `${t(L.EXPORT.assistantLabel)}\n\n${m.content}` : ""))
    .filter(Boolean)
    .join("\n\n");
  const md = `${head}\n${body}\n`;
  const stamp = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, "0")}${String(date.getDate()).padStart(2, "0")}-${String(
    date.getHours()
  ).padStart(2, "0")}${String(date.getMinutes()).padStart(2, "0")}`;
  try {
    const blob = new Blob([md], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = tf(L.EXPORT.fileName, stamp);
    document.body.append(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    setStatus(t(L.EXPORT.exported));
  } catch {
    // Fallback: copy to clipboard if download is blocked in the host webview.
    void writeClipboard(md).then((ok) => setStatus(ok ? t(L.EXPORT.copiedToClip) : t(L.EXPORT.exportFailed), ok ? "" : "error"));
  }
}

function exportAllHistory() {
  const list = loadHistory();
  if (!list.length) {
    setStatus(getLang() === "en" ? "No history to export" : "没有可导出的历史记录", "warn");
    return;
  }
  const date = new Date();
  const lines: string[] = [t(L.EXPORT.allHistoryTitle), ""];
  list.forEach((entry, i) => {
    lines.push(`## ${i + 1}. ${entry.title || "新对话"}`);
    lines.push("");
    lines.push(`*${entry.date}*`);
    lines.push("");
    const body = (Array.isArray(entry.chat) ? entry.chat : [])
      .map((m) => (m.role === "user" ? `${t(L.EXPORT.userLabel)}\n\n${m.content}` : m.role === "assistant" ? `${t(L.EXPORT.assistantLabel)}\n\n${m.content}` : ""))
      .filter(Boolean)
      .join("\n\n");
    lines.push(body || "_（空对话）_");
    lines.push("");
    lines.push("---");
    lines.push("");
  });
  const md = lines.join("\n");
  const stamp = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, "0")}${String(date.getDate()).padStart(2, "0")}-${String(
    date.getHours()
  ).padStart(2, "0")}${String(date.getMinutes()).padStart(2, "0")}`;
  try {
    const blob = new Blob([md], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = tf(L.EXPORT.allHistoryFileName, stamp);
    document.body.append(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    setStatus(getLang() === "en" ? `Exported ${list.length} chats` : `已导出 ${list.length} 条对话`);
  } catch {
    void writeClipboard(md).then((ok) => setStatus(ok ? t(L.EXPORT.copiedToClip) : t(L.EXPORT.exportFailed), ok ? "" : "error"));
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" })[c] as string);
}
